import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
  fetchSFQDetail,
  saveNarrative,
  saveSFQInfo,
  clearCurrentSFQ,
  requestPublish,
  discardDraft,
  fetchPublishedVersion,
} from "../Redux/feasibility";
import { NarrativeView, QuestionsView } from "../FeasibilityEditor/VersionContent";
import { useReviewerCandidates, useOwnerCandidates, pmLabel } from "../usePMCandidates";
import { hasPermission } from "../../../utils/hasPermission";
import { PERM } from "../../../utils/permissions";
import CraRosterEditor from "./CraRosterEditor";
import CraAssessmentToggle, { craAssessmentEnabled } from "./CraAssessmentToggle";
import ConsentToggle from "./ConsentToggle";
import StatusBar from "../FeasibilityEditor/StatusBar";
import PublishApprovalBar from "../FeasibilityEditor/PublishApprovalBar";
import NarrativeEditor from "../FeasibilityEditor/NarrativeEditor";
import SFQQuestionEditor from "../FeasibilityEditor/SFQQuestionEditor";
import AuditTrail from "../FeasibilityEditor/AuditTrail";
import SiteInvitationList from "../SiteInvitationList";
import VersionHistory from "../VersionHistory";
import TemplatePicker from "../TemplatePicker";
import { NARRATIVE_BOX_WIDTH } from "../FeasibilityEditor/narrativeStyles";
import { STATUS_LABELS, isStudyFrozen } from "../FeasibilityEditor/data";

const Wrap = styled.div``;

// 내러티브 상자는 사이트와 같은 폭으로 고정돼 있어 관리자 화면에서는 왼쪽에 붙는다.
// 제목줄까지 같은 폭으로 묶어 화면 가운데에 놓는다. (상자 폭 = 내용 폭 + 테두리 2px)
const NarrativeColumn = styled.div`
  max-width: ${NARRATIVE_BOX_WIDTH + 2}px;
  margin: 0 auto;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 16px;
`;

const StudyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin: 0;
`;

const StudyCode = styled.span`
  font-size: 13px;
  color: #6c757d;
`;

const PmBlock = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6c757d;
`;

const PmSelect = styled.select`
  padding: 3px 6px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 12px;
  background: #fff;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const MetaInput = styled.input`
  padding: 3px 6px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: ${(p) => (p.$wide ? "14px" : "12px")};
  width: ${(p) => (p.$wide ? "340px" : "150px")};
  max-width: 100%;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const LinkBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: #0d6efd;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { text-decoration: underline; }
`;

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 7px 16px;
  border: none;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 2px solid ${(p) => (p.$active ? "#0d6efd" : "transparent")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#495057")};
  margin-bottom: -2px;
  &:hover { color: #0d6efd; }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin: 20px 0 8px;
`;

const SectionHeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const LoadTemplateBtn = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  background: #fff;
  color: #495057;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { filter: brightness(0.96); }
`;

const VersionBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff8e1;
  border: 1px solid #ffe08a;
  border-radius: 6px;
  margin-bottom: 14px;
  font-size: 12.5px;
  color: #7a5c00;
`;

const PublishBtn = styled.button`
  margin-left: auto;
  white-space: nowrap;
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid #0d6efd;
  background: ${(p) => (p.disabled ? "#fff" : "#0d6efd")};
  color: ${(p) => (p.disabled ? "#0d6efd" : "#fff")};
  font-size: 12px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.92); }
`;

const SecondaryBtn = styled.button`
  white-space: nowrap;
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid #adb5bd;
  background: #fff;
  color: #495057;
  font-size: 12px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #f1f3f5; }
`;

// Shown to a CRA who opened a study someone else created: the buttons are simply
// absent, which without a word of explanation reads as a bug.
const ForeignNote = styled.div`
  padding: 9px 14px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 14px;
  font-size: 12.5px;
  color: #495057;
`;

// Finalize onwards. Sits under StatusBar and supersedes the role notes above it: on a
// closed-out study what the viewer can do no longer depends on who they are.
const FrozenNote = styled.div`
  padding: 9px 14px;
  border-left: 3px solid #495057;
  border-radius: 3px;
  background: #f1f3f5;
  margin-bottom: 14px;
  font-size: 12.5px;
  color: #343a40;
`;

// Marks the tabs as showing published content rather than the frozen working copy.
const ReadOnlyNote = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 8px;
`;

const Muted = styled.div`
  padding: 24px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

const SAVE_DEBOUNCE_MS = 2000;

// studyCode / studyTitle / the reviewer / the CRA roster all describe the study rather
// than its content, and they freeze together with everything else once it closes out —
// a finalized study keeps the people it closed out with, SFQ_ADMIN included. The API
// applies the same rule (FROZEN_STATUSES); this only decides whether the 변경 link is
// offered.
const canEditStudyInfo = (sfq, { isOwner, isAdmin }) =>
  (isOwner || isAdmin) && !isStudyFrozen(sfq.status);

// The study's own identity, edited in place in the header. Code and title are edited
// together because a correction to one usually comes with the other, and it keeps the
// header from sprouting two separate 변경 links.
function StudyInfoEditor({ sfq, onDone }) {
  const dispatch = useDispatch();
  const [code, setCode]   = useState(sfq.studyCode  || "");
  const [title, setTitle] = useState(sfq.studyTitle || "");
  const [saving, setSaving] = useState(false);

  const nextCode  = code.trim();
  const nextTitle = title.trim();
  const changed   = nextCode !== sfq.studyCode || nextTitle !== sfq.studyTitle;
  const valid     = !!nextCode && !!nextTitle;

  async function handleSubmit() {
    if (!changed || !valid || saving) return;
    setSaving(true);
    try {
      await dispatch(
        saveSFQInfo({ id: sfq.id, body: { studyCode: nextCode, studyTitle: nextTitle } })
      ).unwrap();
      await dispatch(fetchSFQDetail(sfq.id));
      onDone();
    } catch (e) {
      // Study Code 중복(409)이 가장 흔하다. 서버 메시지가 어느 스터디가 쓰고 있는지 알려준다.
      alert(e?.message || "스터디 정보 저장에 실패했습니다.");
      setSaving(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleSubmit();
    if (e.key === "Escape") onDone();
  }

  return (
    <>
      <MetaInput
        $wide
        autoFocus
        value={title}
        maxLength={200}
        disabled={saving}
        placeholder="Study Title"
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <MetaInput
        value={code}
        maxLength={50}
        disabled={saving}
        placeholder="Study Code"
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <LinkBtn onClick={handleSubmit} disabled={saving || !changed || !valid}>
        {saving ? "저장 중..." : "저장"}
      </LinkBtn>
      <LinkBtn onClick={onDone} disabled={saving}>
        취소
      </LinkBtn>
    </>
  );
}

// Reassigns one of the study's single-value named roles — the reviewer
// (reviewerEmployeeNo) or the owner (createdBy). Split out so the employee list is only
// fetched once the picker is actually opened, rather than on every StudyView mount.
function AssigneeEditor({ sfq, field, noun, useCandidates, onDone }) {
  const dispatch = useDispatch();
  const current = String(sfq[field] ?? "");
  const [selected, setSelected] = useState(current);
  const [saving, setSaving] = useState(false);
  const { candidates, loading, error } = useCandidates();

  const changed = !!selected && selected !== current;

  async function handleSubmit() {
    if (!changed) return;
    const next = candidates.find((emp) => String(emp.employeeNo) === selected);
    const label = next ? pmLabel(next) : selected;
    if (!window.confirm(`${noun}을(를) ${label}(으)로 변경하시겠습니까?`)) return;

    setSaving(true);
    try {
      const res = await dispatch(
        saveSFQInfo({ id: sfq.id, body: { [field]: selected } })
      ).unwrap();
      await dispatch(fetchSFQDetail(sfq.id)); // refresh the derived display name
      // Reassignment saved, but the notification to the new assignee may have failed.
      if (res?.emailWarning) alert(res.emailWarning);
      onDone();
    } catch (e) {
      alert(e?.message || `${noun} 변경에 실패했습니다.`);
      setSaving(false);
    }
  }

  if (error) return <span style={{ fontSize: 12, color: "#dc3545" }}>{error}</span>;

  return (
    <>
      <PmSelect
        autoFocus
        value={selected}
        disabled={loading || saving}
        onChange={(e) => setSelected(e.target.value)}
      >
        <option value="">
          {loading ? "불러오는 중..." : `변경할 ${noun}을(를) 선택해주세요`}
        </option>
        {candidates.map((emp) => (
          <option key={emp.employeeNo} value={emp.employeeNo}>
            {pmLabel(emp)} — {emp.jd}
          </option>
        ))}
      </PmSelect>
      <LinkBtn onClick={handleSubmit} disabled={loading || saving || !changed}>
        {saving ? "저장 중..." : "저장"}
      </LinkBtn>
      <LinkBtn onClick={onDone} disabled={saving}>
        취소
      </LinkBtn>
    </>
  );
}

// The study's reviewer — approves internal review and publishing. The owner (and Admin)
// may reassign it until the study locks. It can never be the owner themselves; the API
// enforces that. Open/closed is decided by StudyView, which keeps the header's editors
// mutually exclusive.
function ReviewerField({ sfq, isOwner, isAdmin, editing, onEdit, onDone }) {
  const canEdit = canEditStudyInfo(sfq, { isOwner, isAdmin });

  return (
    <PmBlock>
      <span>Reviewer:</span>
      {editing ? (
        <AssigneeEditor
          sfq={sfq}
          field="reviewerEmployeeNo"
          noun="Reviewer"
          useCandidates={useReviewerCandidates}
          onDone={onDone}
        />
      ) : (
        <>
          <span style={{ color: sfq.reviewerEmployeeNo ? "#212529" : "#adb5bd" }}>
            {sfq.reviewerEmployeeNo ? `${sfq.reviewerName || "-"} (${sfq.reviewerEmployeeNo})` : "미지정"}
          </span>
          {canEdit && <LinkBtn onClick={onEdit}>변경</LinkBtn>}
        </>
      )}
    </PmBlock>
  );
}

// The owning employee (createdBy) — runs the whole study. Only Admin reassigns it, the
// escape hatch for a study whose owner has left, and only up until the study freezes.
// It can never be the study's own reviewer; the API enforces that.
function OwnerField({ sfq, isAdmin, editing, onEdit, onDone }) {
  const canEdit = isAdmin && !isStudyFrozen(sfq.status);

  return (
    <PmBlock>
      <span>Owner:</span>
      {editing ? (
        <AssigneeEditor
          sfq={sfq}
          field="createdBy"
          noun="Owner"
          useCandidates={useOwnerCandidates}
          onDone={onDone}
        />
      ) : (
        <>
          <span style={{ color: "#212529" }}>
            {sfq.createdByName || sfq.createdBy}
          </span>
          {canEdit && <LinkBtn onClick={onEdit}>변경</LinkBtn>}
        </>
      )}
    </PmBlock>
  );
}

// Body of Overview/Questions while a publish request is pending: the version in
// force, not the frozen working copy. The proposed change opens from the bar above.
function PendingContent({ loading, published, children }) {
  if (loading) return <Muted>Loading...</Muted>;
  if (!published) {
    return <Muted>아직 발행된 버전이 없습니다. 승인되면 첫 버전이 발행됩니다.</Muted>;
  }
  return (
    <>
      <ReadOnlyNote>
        현재 발행되어 적용 중인 <b>V{published.versionNo}</b> 내용입니다.
        승인 대기 중인 변경본은 위의 <b>변경본 보기</b>에서 확인하세요.
      </ReadOnlyNote>
      {children}
    </>
  );
}

export default function StudyView({ sfqId, onSelectInvitation }) {
  const dispatch    = useDispatch();
  const sfq         = useSelector((s) => s.feasibility.currentSFQ);
  const loading     = useSelector((s) => s.feasibility.detailLoading);
  const permissions = useSelector((s) => s.userInfo.permissions);
  const me          = useSelector((s) => s.userInfo.employeeNo);
  const published   = useSelector((s) => s.feasibility.publishedVersion);
  const pubLoading  = useSelector((s) => s.feasibility.publishedVersionLoading);

  const isStudyEdit = hasPermission(permissions, PERM.SFQ_STUDY_EDIT);
  const isAdmin     = hasPermission(permissions, PERM.SFQ_ADMIN);
  // Reviewer 자격은 스터디 지정에서 나온다. 미지정 스터디는 누구도 reviewer가 아니다.
  const isReviewer = !!sfq?.reviewerEmployeeNo && String(sfq.reviewerEmployeeNo) === String(me);
  // Owner도 같은 축이다: SFQ_STUDY_EDIT는 스터디를 '만들' 자격이고, 만들어진 스터디의
  // 개요·질문 편집과 생명주기 전환은 그 스터디의 owner(createdBy)만 할 수 있다.
  const isOwner = isStudyEdit && !!sfq?.createdBy && String(sfq.createdBy) === String(me);
  const isAssignedCRA = !!(sfq?.cras || []).some((c) => String(c.employeeNo) === String(me));
  // 사이트 발송·코멘트·평가는 배정된 CRA 또는 owner가 한다.
  const canWork = isOwner || isAssignedCRA;
  // 개요·질문 편집, 생명주기 전환은 owner만.
  const canAuthor = isOwner;

  const [tab, setTab]     = useState("sites");
  // 머리말의 인라인 편집기(스터디 정보 / Owner / Reviewer / CRA 추가)는 한 번에 하나만 연다.
  // 다른 항목의 [변경]을 누르면 열려 있던 편집기는 입력하던 값과 함께 닫히고 원래 표시로
  // 돌아간다 — 두 곳이 동시에 열려 있으면 어느 쪽이 저장되는지 알 수 없다.
  const [editor, setEditor] = useState(null); // "info" | "owner" | "reviewer" | "cra" | null
  const [qeDirty, setQeDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [revising, setRevising] = useState(false);
  const [pickingNarrative, setPickingNarrative] = useState(false);
  const [editorKey, setEditorKey] = useState(0); // bump to remount editors after discard
  const narrativeTimer    = useRef(null);
  // Latest HTML typed into the editor. The Overview tab unmounts the editor, so
  // this survives a tab round-trip made before the debounced save lands.
  const latestNarrative   = useRef(null);
  const pendingNarrative  = useRef(null); // set while a debounced save is queued

  // 닫기 요청은 자기 자신이 아직 열려 있을 때만 받는다. 저장이 도는 동안 사용자가 다른
  // 항목의 [변경]을 누르면 저장을 시킨 편집기는 이미 닫힌 뒤인데, 뒤늦게 끝난 저장이
  // 그 사이 새로 연 편집기까지 닫아버리면 안 된다.
  const closeEditor = useCallback((which) => {
    setEditor((cur) => (cur === which ? null : cur));
  }, []);

  const flushNarrative = useCallback(() => {
    if (pendingNarrative.current == null) return;
    clearTimeout(narrativeTimer.current);
    const html = pendingNarrative.current;
    pendingNarrative.current = null;
    dispatch(saveNarrative({ id: sfqId, html }));
  }, [sfqId, dispatch]);

  function handleTabChange(next) {
    if (qeDirty && tab === "questions" && next !== "questions") {
      if (!window.confirm("저장하지 않은 변경 사항이 있습니다.\n탭을 이동하면 변경 사항이 사라집니다.\n이동하시겠습니까?")) return;
    }
    if (tab === "overview" && next !== "overview") flushNarrative();
    setTab(next);
  }

  useEffect(() => {
    dispatch(fetchSFQDetail(sfqId));
    return () => {
      flushNarrative();
      latestNarrative.current = null;
      dispatch(clearCurrentSFQ());
    };
  }, [sfqId, dispatch, flushNarrative]);

  // While the working copy is frozen awaiting approval, the tabs show the version
  // that is actually in force. The proposed change is viewed from PublishApprovalBar.
  const pendingPM     = !!sfq?.publishPendingPM;
  const publishedVerId = sfq?.latestVersionId;
  useEffect(() => {
    if (pendingPM && publishedVerId && !published && !pubLoading) {
      dispatch(fetchPublishedVersion({ id: sfqId, versionId: publishedVerId }));
    }
  }, [pendingPM, publishedVerId, published, pubLoading, sfqId, dispatch]);

  const handleNarrativeChange = useCallback((html) => {
    latestNarrative.current  = html;
    pendingNarrative.current = html;
    clearTimeout(narrativeTimer.current);
    narrativeTimer.current = setTimeout(async () => {
      pendingNarrative.current = null;
      setSaving(true);
      try {
        await dispatch(saveNarrative({ id: sfqId, html })).unwrap();
      } finally {
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
  }, [sfqId, dispatch]);

  // A template / prior version chosen in the picker either replaces the narrative or is
  // appended to it. The editor is uncontrolled once mounted, so it has to be remounted
  // with the new content; saving rides the same debounce as ordinary typing.
  function handleNarrativeApply({ html }, mode) {
    const current = latestNarrative.current ?? (sfq?.narrativeHtml || "");
    handleNarrativeChange(mode === "append" ? current + html : html);
    setEditorKey((k) => k + 1);
  }

  // Requesting freezes the working copy server-side, so a debounced narrative save
  // must land first — afterwards it would be refused and the text silently lost.
  async function flushNarrativeNow() {
    if (pendingNarrative.current == null) return;
    clearTimeout(narrativeTimer.current);
    const html = pendingNarrative.current;
    pendingNarrative.current = null;
    await dispatch(saveNarrative({ id: sfqId, html })).unwrap();
  }

  async function handleRequestPublish() {
    if (qeDirty) {
      alert("Questions 탭에 저장하지 않은 변경 사항이 있습니다.\n먼저 저장한 뒤 요청해 주세요.");
      return;
    }
    if (!window.confirm(
      "현재 작업본의 발행 승인을 PM에게 요청합니다.\n" +
      "승인 전까지 내용은 편집할 수 없으며, 승인되면 새 버전이 발행됩니다.\n계속하시겠습니까?"
    )) return;

    setPublishing(true);
    try {
      await flushNarrativeNow();
      const res = await dispatch(requestPublish(sfqId)).unwrap();
      setRevising(false);
      // Request saved, but the PM notification email may have failed to send.
      alert(res?.emailWarning || "PM에게 발행 승인을 요청했습니다.");
    } catch (e) {
      alert(e?.message || "발행 승인 요청에 실패했습니다.");
    } finally {
      setPublishing(false);
    }
  }

  async function handleDiscard(dirty) {
    if (dirty) {
      if (!window.confirm(
        "편집 중인 변경사항을 모두 폐기하고 마지막 발행 버전으로 되돌립니다.\n계속하시겠습니까?"
      )) return;
      setDiscarding(true);
      clearTimeout(narrativeTimer.current);    // drop any queued autosave of discarded text
      pendingNarrative.current = null;
      try {
        await dispatch(discardDraft(sfqId)).unwrap();
        await dispatch(fetchSFQDetail(sfqId)); // reload restored working copy
        latestNarrative.current = null;        // fall back to the restored content
        setEditorKey((k) => k + 1);            // remount editors with restored content
      } catch (e) {
        alert(e?.message || "변경 폐기에 실패했습니다.");
        setDiscarding(false);
        return;
      }
      setDiscarding(false);
    }
    setQeDirty(false);
    setRevising(false);
  }

  if (loading || !sfq) return <div style={{ color: "#6c757d", fontSize: 13 }}>Loading...</div>;

  const isActive = sfq.status === "Active";
  const isDirty  = !!sfq.contentDirty;
  // Finalize onwards the study is closed out: read-only everywhere except StatusBar.
  // canEdit below is already false here (neither Draft nor Active), so this is what the
  // Sites tab and the role notes go by.
  const frozen = isStudyFrozen(sfq.status);
  // A publish request awaiting the reviewer freezes the working copy, so the reviewer
  // approves exactly the content they reviewed. The server refuses edits either way.
  const isPending = !!sfq.publishPendingPM;
  // After Active the content is read-only until the owner explicitly starts a new
  // revision (or one is already in progress = there are unpublished changes).
  const activeEditing = isActive && (revising || isDirty);
  const canEdit  = canAuthor && !isPending && (sfq.status === "Draft" || activeEditing);

  return (
    <Wrap>
      <MetaRow>
        {editor === "info" ? (
          <StudyInfoEditor sfq={sfq} onDone={() => closeEditor("info")} />
        ) : (
          <>
            <StudyTitle>{sfq.studyTitle}</StudyTitle>
            <StudyCode>{sfq.studyCode}</StudyCode>
            {canEditStudyInfo(sfq, { isOwner, isAdmin }) && (
              <LinkBtn onClick={() => setEditor("info")}>변경</LinkBtn>
            )}
          </>
        )}
        <OwnerField
          sfq={sfq}
          isAdmin={isAdmin}
          editing={editor === "owner"}
          onEdit={() => setEditor("owner")}
          onDone={() => closeEditor("owner")}
        />
        <ReviewerField
          sfq={sfq}
          isOwner={isOwner}
          isAdmin={isAdmin}
          editing={editor === "reviewer"}
          onEdit={() => setEditor("reviewer")}
          onDone={() => closeEditor("reviewer")}
        />
        {saving && <span style={{ fontSize: 12, color: "#6c757d" }}>Saving...</span>}
      </MetaRow>

      <CraRosterEditor
        sfq={sfq}
        canEdit={canEditStudyInfo(sfq, { isOwner, isAdmin })}
        adding={editor === "cra"}
        onAdd={() => setEditor("cra")}
        onDone={() => closeEditor("cra")}
      />

      {/* 평가 자체를 쓰지 않는 스터디도 있다. 꺼두면 사이트 응답만 수집한다. */}
      <CraAssessmentToggle sfq={sfq} canEdit={canEditStudyInfo(sfq, { isOwner, isAdmin })} />
      <ConsentToggle sfq={sfq} canEdit={canEditStudyInfo(sfq, { isOwner, isAdmin })} />

      {/* 아래 두 안내는 "무엇을 할 수 있는가"를 역할로 설명한다. 종료된 스터디에서는
          누구든 아무것도 못 하므로, 아래 FrozenNote가 대신 말한다. */}

      {/* CRA로 배정됐으나 개요/질문은 편집할 수 없는 경우 안내. 사이트 작업은 Sites 탭에서. */}
      {!frozen && isAssignedCRA && !isOwner && (
        <ForeignNote>
          이 Study에 <b>CRA로 배정</b>되어 있습니다. 개요·질문 편집과 상태 전환은
          Owner(<b>{sfq.createdByName || sfq.createdBy}</b>)의 몫이며,
          CRA는 <b>Sites 탭</b>에서 사이트 발송·코멘트·평가를 할 수 있습니다.
        </ForeignNote>
      )}

      {/* Owner도 CRA도 아닌 열람자(Reviewer/QA/Admin). 모든 탭이 열리되 대부분 읽기 전용이다. */}
      {!frozen && !isOwner && !isAssignedCRA && (
        <ForeignNote>
          조회 권한으로 열람 중입니다. 내용 편집·사이트 발송은 Owner
          (<b>{sfq.createdByName || sfq.createdBy}</b>)와 배정된 CRA만 할 수 있습니다.
          {isReviewer && " 회원님은 이 Study의 Reviewer로, 내부검토·발행 승인을 수행합니다."}
        </ForeignNote>
      )}

      <StatusBar sfq={sfq} isOwner={isOwner} isReviewer={isReviewer} isAdmin={isAdmin} />

      {frozen && (
        <FrozenNote>
          이 Study는 <b>{STATUS_LABELS[sfq.status] || sfq.status}</b> 상태로 종료되어{" "}
          <b>읽기 전용</b>입니다. 조회와 PDF/Word 내보내기만 가능하며, 개요·질문 편집,
          사이트 추가·발송, 평가·코멘트, 담당자 변경은 할 수 없습니다.
          {sfq.status === "Archived"
            ? " Archived는 종료 상태로, 이전 상태로 되돌릴 수 없습니다."
            : " 상태 전환(위 버튼)은 계속 가능합니다."}
        </FrozenNote>
      )}

      <PublishApprovalBar sfq={sfq} isOwner={isOwner} isReviewer={isReviewer} />

      {/* 스터디를 여는 것 자체가 이미 권한이므로(SFQ_ACCESS) 탭을 더 가리지 않는다.
          편집 차단은 canEdit/canWork가 각 탭 안에서 한다 — 열람자는 읽기 전용으로 본다. */}
      <TabBar>
        <Tab $active={tab === "sites"}     onClick={() => handleTabChange("sites")}>Sites</Tab>
        <Tab $active={tab === "overview"}  onClick={() => handleTabChange("overview")}>Study Overview</Tab>
        <Tab $active={tab === "questions"} onClick={() => handleTabChange("questions")}>Questions</Tab>
        <Tab $active={tab === "history"}   onClick={() => handleTabChange("history")}>Version History</Tab>
        <Tab $active={tab === "audit"}     onClick={() => handleTabChange("audit")}>Audit Trail</Tab>
      </TabBar>

      {tab === "sites" && (
        <SiteInvitationList
          sfqId={sfqId}
          onSelect={onSelectInvitation}
          sfqStatus={sfq.status}
          canWork={canWork && !frozen}
          craEnabled={craAssessmentEnabled(sfq)}
        />
      )}

      {/* While a request is pending, PublishApprovalBar carries the state and actions. */}
      {isActive && canAuthor && !isPending && (tab === "overview" || tab === "questions") && (
        activeEditing ? (
          <VersionBanner>
            <span>
              <b>새 버전 편집 중</b>입니다. 편집 내용은 작업본에만 저장되며,
              <b> Reviewer 승인</b> 후 다음 발송부터 반영됩니다. 이미 발송된 사이트는 받은 버전을 그대로 유지합니다.
            </span>
            <PublishBtn onClick={handleRequestPublish} disabled={!isDirty || publishing || discarding}>
              {publishing ? "요청 중..." : "발행 승인 요청"}
            </PublishBtn>
            <SecondaryBtn onClick={() => handleDiscard(isDirty)} disabled={publishing || discarding}>
              {discarding ? "취소 중..." : "취소(변경 폐기)"}
            </SecondaryBtn>
          </VersionBanner>
        ) : (
          <VersionBanner>
            <span>
              이 Study는 <b>Active</b> 상태로, 내용은 <b>읽기 전용</b>입니다.
              수정하려면 <b>새 버전 만들기</b>를 눌러 새 버전을 작성하세요.
              이미 발송된 사이트는 받은 버전을 그대로 유지합니다.
            </span>
            <PublishBtn onClick={() => setRevising(true)}>새 버전 만들기</PublishBtn>
          </VersionBanner>
        )
      )}

      {tab === "overview" && (
        <NarrativeColumn>
          <SectionHeadRow>
            <SectionLabel>Study Overview / Protocol Summary</SectionLabel>
            {canEdit && (
              <LoadTemplateBtn onClick={() => setPickingNarrative(true)}>
                템플릿 불러오기
              </LoadTemplateBtn>
            )}
          </SectionHeadRow>
          {isPending ? (
            <PendingContent loading={pubLoading} published={published}>
              <NarrativeView html={published?.narrativeHtml} />
            </PendingContent>
          ) : (
            <NarrativeEditor
              key={editorKey}
              value={latestNarrative.current ?? (sfq.narrativeHtml || "")}
              onChange={handleNarrativeChange}
              readOnly={!canEdit}
              sfqId={sfq.id}
            />
          )}
          {pickingNarrative && (
            <TemplatePicker
              kind="narrative"
              hasContent={!!(latestNarrative.current ?? sfq.narrativeHtml)}
              onApply={handleNarrativeApply}
              onClose={() => setPickingNarrative(false)}
            />
          )}
        </NarrativeColumn>
      )}

      {tab === "questions" && (
        isPending ? (
          <PendingContent loading={pubLoading} published={published}>
            <QuestionsView questions={published?.questions} />
          </PendingContent>
        ) : (
          <SFQQuestionEditor key={editorKey} sfqId={sfq.id} editable={canEdit} onDirtyChange={setQeDirty} />
        )
      )}

      {tab === "history" && <VersionHistory sfqId={sfq.id} />}

      {tab === "audit" && <AuditTrail sfqId={sfq.id} />}
    </Wrap>
  );
}
